import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { Button } from 'gdpp-components/dist/cjs/components/atoms'
import '@fil-global/gds-webcomponents/fil-icon/fil-icon.component'
import '@fil-global/gds-webcomponents/fil-button-group/fil-button-group.component'

import useComponentVisible from '../../hoc/useComponentVisible'

import './style.scss'

const Modal = ({
  triggerButtonConfig,
  size,
  extraClasses,
  zIndex,
  modalHeader,
  modalHeaderClasses,
  primaryLabel,
  secondaryType,
  secondaryLabel,
  secondaryUrl,
  imageSrc,
  imageAlt,
  stickyFooter,
  onModalClose,
  dialogContent,
  children,
  kind
}) => {
  const { ref, isComponentVisible: modalStatus, setIsComponentVisible: setModalStatus } = useComponentVisible(false)
  const { label: triggerButtonLabel, extraClasses: triggerButtonClasses, ...restTriggerButtonConfig } = triggerButtonConfig
  const [iconButtonStatus, setIconButtonStatus] = useState(false)
  const imageTemplate = () => {
    return (
      <div className="dialog__image">
        <img src={imageSrc} alt={imageAlt} />
      </div>
    )
  }

  const secondaryCtaTemplate = () => {
    if (secondaryType === 'button') {
      return <Button kind="tertiary" size="standard" label={secondaryLabel} />
    }

    if (secondaryType === 'link') {
      return (
        <span className="fil-dialog__cta-link">
          <Button size="standard" kind="textlink--padding" url={secondaryUrl} label={secondaryLabel} />
        </span>
      )
    }

    return null
  }

  const handleOpen = () => {
    setModalStatus(true)
  }

  const handleClose = () => {
    setModalStatus(false)
    onModalClose()
  }

  const handleIconButtonMouseOver = () => {
    setIconButtonStatus(true)
  }

  const handleIconButtonMouseOut = () => {
    setIconButtonStatus(false)
  }

  useEffect(() => {}, [])

  return (
    <React.Fragment>
      <Button
        extraClasses={classnames(triggerButtonClasses, {
          'fil-reveal-overlay-tooltip-button': kind === 'tooltip'
        })}
        {...{
          kind: 'textlink',
          icon: 'custom-info-circle',
          iconPosition: 'left',
          iconSize: '20',
          onClick: handleOpen,
          ...restTriggerButtonConfig
        }}
        {...(kind === 'tooltip'
          ? {
              kind: 'icon',
              iconSize: '16',
              iconColor: 'blue-500',
              icon: 'custom-info-circle',
              onMouseOver: handleIconButtonMouseOver,
              onMouseOut: handleIconButtonMouseOut,
              filled: iconButtonStatus
            }
          : {})}
        label={triggerButtonLabel}
      />
      <div
        className="fil-reveal-overlay"
        style={{
          display: modalStatus ? 'block' : 'none',
          zIndex
        }}
      >
        <div
          className={classnames('fil-reveal', size, extraClasses)}
          style={{
            display: modalStatus ? 'block' : 'none',
            zIndex: zIndex + 1
          }}
          ref={ref}
        >
          <div
            className={classnames('fil-dialog', {
              'fil-dialog--fixed-mobile': stickyFooter
            })}
            role="dialog"
            aria-modal="true"
          >
            <button
              onClick={handleClose}
              className={classnames('close-button', {
                'close-button--small': size !== 'large'
              })}
              aria-label="Close modal"
              type="button"
              tabIndex="0"
            >
              <fil-icon icon="close" size="16" />
            </button>
            <h3
              className={classnames('dialog-heading', {
                'dialog-heading--large': size === 'large',
                [modalHeaderClasses]: modalHeaderClasses
              })}
            >
              {modalHeader}
            </h3>
            {children ? (
              <div className="fil-dialog__content">{children}</div>
            ) : (
              <React.Fragment>
                {imageSrc && imageTemplate()}
                {typeof dialogContent === 'string' ? (
                  <div className="fil-dialog__content" dangerouslySetInnerHTML={{ __html: dialogContent }} />
                ) : (
                  <div className="fil-dialog__content">{dialogContent}</div>
                )}
              </React.Fragment>
            )}
            <div className="fil-dialog__cta">
              <fil-button-group position="center" size="standard">
                {primaryLabel && <Button size="standard" label={primaryLabel} />}
                {secondaryType && secondaryCtaTemplate()}
              </fil-button-group>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

Modal.propTypes = {
  kind: PropTypes.string,
  triggerButtonConfig: PropTypes.object,
  modalHeader: PropTypes.string,
  modalHeaderClasses: PropTypes.string,
  onModalClose: PropTypes.func,
  size: PropTypes.string,
  extraClasses: PropTypes.string,
  zIndex: PropTypes.number,
  primaryLabel: PropTypes.string,
  secondaryType: PropTypes.string,
  secondaryLabel: PropTypes.string,
  secondaryUrl: PropTypes.string,
  imageSrc: PropTypes.string,
  imageAlt: PropTypes.string,
  stickyFooter: PropTypes.bool,
  dialogContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
}

Modal.defaultProps = {
  kind: '',
  triggerButtonConfig: {},
  modalHeader: '',
  modalHeaderClasses: '',
  onModalClose: () => {},
  size: 'large',
  extraClasses: '',
  zIndex: 50,
  primaryLabel: '',
  secondaryType: '',
  secondaryLabel: '',
  secondaryUrl: '',
  imageSrc: '',
  imageAlt: '',
  stickyFooter: false,
  dialogContent: '',
  children: null
}

export default Modal
